/**
 * "Configure video for an event" form (FR-VIDEO-04/05).
 *
 * Client-side validation catches the obvious ("pick an event", "paste a link or
 * a stream id", "the link must start with https://"); the server's provider
 * adapter is the authority on host policy and its 400 renders inline on the
 * offending field.
 */
import { useState } from 'react';

import { useToast } from '../../app/toast-context';
import { DeskSection } from '../../components/desk-section';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type { VideoProviderCapability } from '../../lib/api-types';

import type { VideoSettingsController } from './use-video-settings';

const HTTPS_PREFIX = 'https://';

function providerPlaceholder(providerName: string): string {
  return providerName === 'hudl'
    ? 'https://www.hudl.com/embed/…'
    : 'https://www.youtube.com/watch?v=…';
}

export function EventEmbedForm({
  controller,
  provider,
}: {
  controller: VideoSettingsController;
  provider: VideoProviderCapability;
}): JSX.Element {
  const { showToast } = useToast();
  const [eventId, setEventId] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [streamId, setStreamId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (eventId === '') next.event_id = 'Choose the event this video belongs to.';
    if (embedUrl.trim() === '' && streamId.trim() === '') {
      next.embed_url = 'Paste an embed link or enter a stream id.';
    } else if (embedUrl.trim() !== '' && !embedUrl.trim().startsWith(HTTPS_PREFIX)) {
      next.embed_url = 'The embed link must start with https://.';
    }
    return next;
  }

  async function submit(): Promise<void> {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    setIsSaving(true);
    try {
      await controller.saveEmbed(eventId, {
        provider: provider.provider_name as 'hudl' | 'youtube',
        embed_url: embedUrl.trim() === '' ? null : embedUrl.trim(),
        stream_id: streamId.trim() === '' ? null : streamId.trim(),
      });
      setEmbedUrl('');
      setStreamId('');
      setErrors({});
      showToast('Event video saved.', 'success');
    } catch (caught) {
      if (ApiError.isApiError(caught)) {
        const field =
          typeof caught.details?.field === 'string' ? caught.details.field : 'embed_url';
        setErrors({ [field]: caught.message });
      } else {
        setErrors({ embed_url: 'The event video could not be saved. Please try again.' });
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <DeskSection
      title="Configure video for an event"
      description={`Saving replaces any existing ${provider.label} video for that event. Coaching staff always see it; fans see it only when the provider is fan-visible.`}
    >
      <UnsavedChangesGuard
        isDirty={eventId !== '' || embedUrl.trim() !== '' || streamId.trim() !== ''}
      />
      <form
        className="team-settings__form team-settings__embed-form desk-form desk-form--two-col desk-form--stacked"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div className={`field${errors.event_id === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="embed-event">
            Event
          </label>
          <select
            id="embed-event"
            className="field__control"
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
            aria-invalid={errors.event_id === undefined ? undefined : true}
          >
            <option value="">Select an event…</option>
            {controller.events.map((event) => (
              <option key={event.event_id} value={event.event_id}>
                {event.name}
              </option>
            ))}
          </select>
          {errors.event_id === undefined ? null : (
            <span className="field__error" role="alert">
              {errors.event_id}
            </span>
          )}
        </div>

        <div className={`field${errors.embed_url === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="embed-url">
            Embed link
          </label>
          <input
            id="embed-url"
            className="field__control"
            type="url"
            inputMode="url"
            maxLength={2000}
            placeholder={providerPlaceholder(provider.provider_name)}
            value={embedUrl}
            onChange={(event) => setEmbedUrl(event.target.value)}
            aria-invalid={errors.embed_url === undefined ? undefined : true}
          />
          {errors.embed_url === undefined ? (
            <span className="field__hint">
              A YouTube watch, share or live link is converted to an embed link automatically.
            </span>
          ) : (
            <span className="field__error" role="alert">
              {errors.embed_url}
            </span>
          )}
        </div>

        <div className={`field${errors.stream_id === undefined ? '' : ' field--invalid'}`}>
          <label className="field__label" htmlFor="embed-stream-id">
            Stream id (optional)
          </label>
          <input
            id="embed-stream-id"
            className="field__control"
            type="text"
            maxLength={128}
            placeholder="dQw4w9WgXcQ"
            value={streamId}
            onChange={(event) => setStreamId(event.target.value)}
            aria-invalid={errors.stream_id === undefined ? undefined : true}
          />
          {errors.stream_id === undefined ? null : (
            <span className="field__error" role="alert">
              {errors.stream_id}
            </span>
          )}
        </div>

        <div className="desk-form__actions desk-form__full">
          <button type="submit" className="btn btn--primary" disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save event video'}
          </button>
        </div>
      </form>
    </DeskSection>
  );
}
